import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Copy, Check, Gift, Share2, Sparkles, Trophy } from 'lucide-react';
import { toast } from 'sonner';
import { api } from './api';
import { logger } from '../lib/logger';

function useReferralData() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get('/api/referrals/me').then(setData).catch((e) => logger.error('refer load', e)).finally(() => setLoading(false));
  }, []);
  return { data, loading };
}

function ProgressBar({ count, threshold }) {
  const pct = Math.min(100, (count / threshold) * 100);
  return (
    <div className="w-full h-3 rounded-full bg-foreground/10 overflow-hidden" data-testid="refer-progress">
      <div
        className="h-full bg-[#004C54] transition-all duration-500"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

export default function ReferPage() {
  const { data, loading } = useReferralData();
  const [copied, setCopied] = useState(false);

  const copyLink = () => {
    if (!data?.referral_url) return;
    navigator.clipboard.writeText(data.referral_url);
    setCopied(true);
    toast.success('Referral link copied!');
    setTimeout(() => setCopied(false), 2200);
  };

  const nativeShare = async () => {
    if (!navigator.share) {
      copyLink();
      return;
    }
    try {
      await navigator.share({
        title: 'Apex Athletic Mind',
        text: 'Train your mental game with Coach Lewis. Try it free —',
        url: data.referral_url,
      });
    } catch (e) {
      logger.info('share dismissed', e);
    }
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto space-y-4 p-4">
        <div className="h-32 rounded-lg bg-foreground/5 animate-pulse" />
        <div className="h-24 rounded-lg bg-foreground/5 animate-pulse" />
      </div>
    );
  }
  if (!data) return null;

  const { referral_url, count, threshold, remaining, reward_days, reward_unlocked } = data;

  return (
    <div className="max-w-3xl mx-auto space-y-6 p-4 sm:p-0" data-testid="refer-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Refer Friends, Unlock {reward_days} Days Free</h1>
        <p className="text-foreground/70 mt-1">Share Coach Lewis with {threshold} teammates and your next month of Apex AI is on the house.</p>
      </div>

      {reward_unlocked ? (
        <Card className="border-2 border-[#004C54] bg-[#004C54]/5" data-testid="refer-reward-card">
          <CardContent className="p-6 flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-[#004C54] flex items-center justify-center shrink-0">
              <Trophy className="h-6 w-6 text-white" />
            </div>
            <div>
              <div className="font-heading text-xl font-bold">Reward unlocked!</div>
              <p className="text-sm text-foreground/70 mt-1">You hit {threshold} referrals — {reward_days} days of Apex AI are active right now. Keep referring for ongoing love from Coach Lewis.</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-2 border-[#004C54]/20">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Gift className="h-5 w-5 text-[#004C54]" />
                <span className="font-mono-accent uppercase tracking-[0.18em] text-[11px] font-bold text-[#004C54]">Your Progress</span>
              </div>
              <span className="text-sm font-semibold" data-testid="refer-counter">{count} / {threshold} signups</span>
            </div>
            <ProgressBar count={count} threshold={threshold} />
            <p className="text-sm text-foreground/70">
              {remaining > 0 ? (
                <><strong className="text-foreground">{remaining} more</strong> {remaining === 1 ? 'signup' : 'signups'} until {reward_days} days of Apex AI unlock.</>
              ) : (
                <>Reward unlocking — refresh in a few seconds.</>
              )}
            </p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-6 space-y-4">
          <div>
            <div className="font-heading text-lg font-bold">Your Referral Link</div>
            <p className="text-xs text-foreground/60 mt-0.5">Send it to a teammate, parent, or coach. They sign up free — you both win.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              value={referral_url}
              readOnly
              className="font-mono text-xs sm:text-sm"
              data-testid="refer-url-input"
              onFocus={(e) => e.target.select()}
            />
            <div className="flex gap-2">
              <Button onClick={copyLink} variant="outline" data-testid="refer-copy">
                {copied ? <Check className="h-4 w-4 mr-1.5" /> : <Copy className="h-4 w-4 mr-1.5" />}
                {copied ? 'Copied' : 'Copy'}
              </Button>
              <Button onClick={nativeShare} className="bg-[#004C54] hover:bg-[#003a40] text-white" data-testid="refer-share">
                <Share2 className="h-4 w-4 mr-1.5" /> Share
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-foreground/[0.03]">
        <CardContent className="p-6 space-y-2">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-[#004C54]" />
            <div className="font-semibold">How it works</div>
          </div>
          <ol className="space-y-1.5 text-sm text-foreground/70 list-decimal pl-5">
            <li>Share your link with teammates, friends, parents, or your coach.</li>
            <li>When {threshold} of them sign up (free), you unlock {reward_days} days of Apex AI on the house.</li>
            <li>The reward activates automatically — no codes, no waiting.</li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
}
